/**
 * Phase 67 + 87ah — PDF Devis Traiteur (Offre, Confirmation, Facture)
 *
 * 3 modes via prop `mode` :
 *  - OFFER         : "OFFRE / DEVIS"  (en-tête doré)
 *  - CONFIRMATION  : "CONFIRMATION"   (en-tête vert)
 *  - INVOICE       : "FACTURE"        (en-tête noir — aperçu)
 *
 * Phase 87ah — Refactor souplesse :
 *  - Adresse CLIENT placée en haut-droite (norme suisse, fenêtre enveloppe C5/6)
 *  - Lignes de type SECTION (titre doré) et NOTE (encart italique) supportées
 *  - Lignes `optional` : affichées en italique gris avec mention "à confirmer", hors total
 *  - Bloc "Frais additionnels" (livraison/service…) sous le tableau, avant les totaux
 */
import React from "react";
import { Document, Page, View, Text, Image, StyleSheet } from "@react-pdf/renderer";
import { COMPANY_HEADER } from "../lib/company";

const MODE_META = {
  OFFER: { title: "OFFRE / DEVIS", color: "#C1A063", subtitle: "Proposition tarifaire" },
  CONFIRMATION: { title: "CONFIRMATION", color: "#1F4E3D", subtitle: "Offre acceptée" },
  INVOICE: { title: "FACTURE", color: "#1A1C1A", subtitle: "Document fiscal" },
};

const fmtCHF = (n) => `${(Number(n) || 0).toFixed(2)}`;
const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("fr-CH", { day: "2-digit", month: "long", year: "numeric" });
  } catch { return iso; }
};
const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("fr-CH", { day: "2-digit", month: "long", year: "numeric" }) +
      " à " + d.toLocaleTimeString("fr-CH", { hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

const styles = StyleSheet.create({
  page: { padding: 36, fontFamily: "Helvetica", fontSize: 9, color: "#1A1C1A" },

  // Header row : logo + créditeur (gauche) + titre OFFRE/DEVIS (droite)
  headerRow: { flexDirection: "row", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 },
  senderBlock: { flexDirection: "row", alignItems: "flex-start", gap: 10, maxWidth: "58%" },
  senderLogo: { width: 55, height: 55 },
  senderInfo: { flexDirection: "column" },
  senderName: { fontSize: 15, fontFamily: "Helvetica-Bold", marginBottom: 4, letterSpacing: 0.6 },
  senderMeta: { fontSize: 8, color: "#575A57", lineHeight: 1.4 },

  // Titre OFFRE/DEVIS en haut-droite (comme référence)
  titleRight: { flexDirection: "column", alignItems: "flex-end", maxWidth: "40%" },
  titleMain: { fontSize: 22, fontFamily: "Helvetica-Bold", letterSpacing: 1.2 },
  titleSub: { fontSize: 8.5, color: "#575A57", marginTop: 1, marginBottom: 6 },
  titleRef: { fontSize: 11, fontFamily: "Helvetica-Bold", marginTop: 2 },
  titleDate: { fontSize: 8.5, color: "#575A57", marginTop: 1 },

  // Dual-cards ÉMETTEUR + CLIENT sous le header
  partiesRow: { flexDirection: "row", gap: 12, marginBottom: 14 },
  partyCard: { flex: 1, padding: 10, backgroundColor: "#F4F4F0" },
  partyLabel: { fontSize: 7, color: "#A3A6A3", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 4 },
  partyName: { fontSize: 11, fontFamily: "Helvetica-Bold", marginBottom: 3 },
  partyLine: { fontSize: 9, color: "#1A1C1A", lineHeight: 1.35 },

  // Event
  eventBox: { marginBottom: 14, padding: 10, borderWidth: 1, borderColor: "#C1A063", borderStyle: "solid", backgroundColor: "#FBF4E9" },
  eventTitle: { fontSize: 9, fontFamily: "Helvetica-Bold", color: "#8C6B2F", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 4 },
  eventGrid: { flexDirection: "row", flexWrap: "wrap", gap: 12 },
  eventItem: { flexDirection: "column", minWidth: 100 },
  eventLabel: { fontSize: 7, color: "#A3A6A3", textTransform: "uppercase", letterSpacing: 0.4 },
  eventValue: { fontSize: 10, color: "#1A1C1A", fontFamily: "Helvetica-Bold", marginTop: 1 },

  // Table
  table: { borderTop: "1pt solid #E5E5E0", borderLeft: "1pt solid #E5E5E0", borderRight: "1pt solid #E5E5E0", marginBottom: 8 },
  th: { flexDirection: "row", backgroundColor: "#F4F4F0", padding: 6, borderBottom: "1pt solid #E5E5E0" },
  thCell: { fontFamily: "Helvetica-Bold", fontSize: 8, color: "#575A57", textTransform: "uppercase", letterSpacing: 0.5 },
  tr: { flexDirection: "row", padding: 6, borderBottom: "1pt solid #E5E5E0", alignItems: "flex-start" },
  td: { fontSize: 9 },
  tdMuted: { fontSize: 8.5, color: "#737373", fontFamily: "Helvetica-Oblique" },
  // Section header dans le tableau
  sectionRow: { flexDirection: "row", padding: 8, borderBottom: "1pt solid #C1A063", borderTop: "1pt solid #C1A063", backgroundColor: "#FBF4E9" },
  sectionText: { flex: 1, fontSize: 10, fontFamily: "Helvetica-Bold", color: "#8C6B2F", textTransform: "uppercase", letterSpacing: 0.8 },
  // Note row dans le tableau
  noteRow: { flexDirection: "row", padding: 8, borderBottom: "1pt solid #E5E5E0", backgroundColor: "#FAFAF7" },
  noteText: { flex: 1, fontSize: 9, color: "#575A57", fontFamily: "Helvetica-Oblique", lineHeight: 1.4 },
  // Mention optional
  optionalTag: { fontSize: 7, color: "#8C6B2F", fontFamily: "Helvetica-Oblique", marginTop: 2 },
  lineNotes: { fontSize: 7.5, color: "#737373", fontFamily: "Helvetica-Oblique", marginTop: 2 },

  // Fees
  feesBox: { marginBottom: 8, padding: 8, backgroundColor: "#FAFAF7", borderLeft: "3pt solid #8C6B2F" },
  feesTitle: { fontSize: 8, fontFamily: "Helvetica-Bold", color: "#8C6B2F", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 4 },
  feeRow: { flexDirection: "row", justifyContent: "space-between", paddingVertical: 1.5 },
  feeLabel: { fontSize: 9, color: "#1A1C1A", flex: 3 },
  feeVat: { fontSize: 8, color: "#575A57", flex: 0.6, textAlign: "right" },
  feeAmount: { fontSize: 9, fontFamily: "Helvetica-Bold", flex: 1, textAlign: "right" },

  // Totals
  totals: { alignSelf: "flex-end", width: "55%", marginTop: 6 },
  totalRow: { flexDirection: "row", justifyContent: "space-between", paddingVertical: 2 },
  totalLabel: { fontSize: 9, color: "#575A57" },
  totalValue: { fontSize: 9, fontFamily: "Helvetica-Bold" },
  totalGrand: { flexDirection: "row", justifyContent: "space-between", paddingTop: 6, marginTop: 4, borderTop: "1.5pt solid #1A1C1A" },

  // Notes
  notesBox: { marginTop: 14, padding: 10, backgroundColor: "#FAFAF7", borderLeft: "3pt solid #1F4E3D" },
  notesLabel: { fontSize: 8, color: "#A3A6A3", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 3 },

  // Signature
  signatureRow: { flexDirection: "row", gap: 18, marginTop: 30 },
  signatureBlock: { flex: 1 },
  signatureLabel: { fontSize: 8, color: "#575A57", marginBottom: 30 },
  signatureLine: { borderTop: "0.5pt solid #1A1C1A", paddingTop: 4, fontSize: 8, color: "#575A57" },

  // Footer
  footer: { position: "absolute", bottom: 24, left: 36, right: 36, fontSize: 7, color: "#A3A6A3", textAlign: "center", borderTop: "0.5pt solid #E5E5E0", paddingTop: 6 },
});

/**
 * Calcule les totaux côté client (miroir de _compute_totals backend).
 * Skip lignes SECTION/NOTE et optional. Ajoute additional_fees.
 */
function computeTotals(quote) {
  const lines = quote?.lines || [];
  const fees = quote?.additional_fees || [];
  let totalHt = 0;
  const vatGroups = {};
  lines.forEach((ln) => {
    if (ln.line_type === "SECTION" || ln.line_type === "NOTE") return;
    if (ln.optional) return;
    const lineHt = Number(ln.quantity || 0) * Number(ln.unit_price || 0);
    totalHt += lineHt;
    const r = Number(ln.vat_rate || 0);
    vatGroups[r] = (vatGroups[r] || 0) + lineHt;
  });
  const discountPct = Number(quote?.discount_percent || 0);
  const discountAmount = discountPct > 0 ? totalHt * discountPct / 100 : 0;
  if (discountPct > 0) {
    Object.keys(vatGroups).forEach((k) => { vatGroups[k] *= (1 - discountPct / 100); });
  }
  let feesTotalHt = 0;
  fees.forEach((f) => {
    const amount = Number(f.amount || 0);
    if (!amount) return;
    feesTotalHt += amount;
    const r = Number(f.vat_rate || 0);
    vatGroups[r] = (vatGroups[r] || 0) + amount;
  });
  const htAfter = totalHt - discountAmount + feesTotalHt;
  let vatTotal = 0;
  const vatByRate = {};
  Object.entries(vatGroups).forEach(([r, base]) => {
    const v = base * Number(r) / 100;
    vatTotal += v;
    if (base > 0) vatByRate[r] = v;
  });
  return {
    ht_initial: totalHt,
    discount: discountAmount,
    fees_total_ht: feesTotalHt,
    ht: htAfter,
    vat: vatTotal,
    ttc: htAfter + vatTotal,
    vatGroups: vatByRate,
  };
}

export function CateringQuotePdf({ quote, settings, totals: totalsProp, mode = "OFFER" }) {
  const m = MODE_META[mode] || MODE_META.OFFER;
  const totals = totalsProp || computeTotals(quote);
  // Phase 101eq.2 (Feb 2026) — Fix : n'exclure que les lignes VIDES (ni description
  // ni montant). Une ligne offerte (amount=0 avec description) DOIT s'afficher
  // pour signaler l'offert au client dans le PDF.
  const fees = (quote.additional_fees || []).filter((f) => {
    const hasDesc = (f.description || "").trim().length > 0;
    const hasAmount = Number(f.amount || 0) !== 0;
    return hasDesc || hasAmount;
  });
  const isCH = !quote.client_country || quote.client_country === "CH" || quote.client_country === "Suisse";

  return (
    <Document>
      <Page size="A4" style={styles.page}>

        {/* === EN-TÊTE : Émetteur (gauche) + Titre OFFRE/DEVIS (droite) === */}
        <View style={styles.headerRow}>
          <View style={styles.senderBlock}>
            {settings?.company_logo_url
              ? <Image src={settings.company_logo_url} style={styles.senderLogo} />
              : <Image src={`${typeof window !== "undefined" ? window.location.origin : ""}/logo-maisonthai-pdf.png`} style={styles.senderLogo} />}
            <View style={styles.senderInfo}>
              <Image
                src={`${typeof window !== "undefined" ? window.location.origin : ""}/company-name-sayonara.png`}
                style={{ width: 149, height: 15, marginBottom: 4 }}
              />
              {(settings?.company_address || "").split("\n").filter(Boolean).map((line, i) => (
                <Text key={i} style={styles.senderMeta}>{line}</Text>
              ))}
              {settings?.company_postal_code && settings?.company_city && (
                <Text style={styles.senderMeta}>CH-{settings.company_postal_code} {settings.company_city}</Text>
              )}
              {settings?.company_phone && <Text style={styles.senderMeta}>Tél {settings.company_phone}</Text>}
              {settings?.company_vat_number && <Text style={styles.senderMeta}>TVA : {settings.company_vat_number}</Text>}
            </View>
          </View>

          {/* Titre OFFRE / DEVIS en haut-droite */}
          <View style={styles.titleRight}>
            <Text style={[styles.titleMain, { color: m.color }]}>{m.title}</Text>
            <Text style={styles.titleSub}>{m.subtitle}</Text>
            <Text style={styles.titleRef}>N° {quote.reference}</Text>
            <Text style={styles.titleDate}>{fmtDate(quote.quote_date)}</Text>
            {quote.validity_until && mode === "OFFER" && (
              <Text style={styles.titleDate}>Valable jusqu&apos;au {fmtDate(quote.validity_until)}</Text>
            )}
            {mode === "CONFIRMATION" && quote.accepted_at && (
              <Text style={styles.titleDate}>Accepté le {fmtDate(quote.accepted_at)}</Text>
            )}
          </View>
        </View>

        {/* === Cartes ÉMETTEUR + CLIENT côte à côte === */}
        <View style={styles.partiesRow}>
          <View style={styles.partyCard}>
            <Text style={styles.partyLabel}>Émetteur</Text>
            <Text style={styles.partyName}>{COMPANY_HEADER}</Text>
            {(settings?.company_address || "").split("\n").filter(Boolean).slice(0, 1).map((line, i) => (
              <Text key={i} style={styles.partyLine}>{line}</Text>
            ))}
            {settings?.company_postal_code && settings?.company_city && (
              <Text style={styles.partyLine}>CH-{settings.company_postal_code} {settings.company_city}</Text>
            )}
            {settings?.company_phone && <Text style={styles.partyLine}>Tél {settings.company_phone}</Text>}
          </View>
          <View style={styles.partyCard}>
            <Text style={styles.partyLabel}>Client</Text>
            {quote.client_company ? (
              <>
                <Text style={styles.partyName}>{quote.client_company}</Text>
                {quote.client_name ? <Text style={styles.partyLine}>{quote.client_name}</Text> : null}
              </>
            ) : (
              <Text style={styles.partyName}>{quote.client_name || "Client"}</Text>
            )}
            {quote.client_address ? <Text style={styles.partyLine}>{quote.client_address}</Text> : null}
            {(quote.client_postal_code || quote.client_city) && (
              <Text style={styles.partyLine}>
                {isCH ? "CH-" : ""}{quote.client_postal_code} {quote.client_city}
              </Text>
            )}
            {!isCH && quote.client_country ? <Text style={styles.partyLine}>{quote.client_country}</Text> : null}
            {quote.client_phone ? <Text style={styles.partyLine}>Tél : {quote.client_phone}</Text> : null}
            {quote.client_email ? <Text style={styles.partyLine}>{quote.client_email}</Text> : null}
          </View>
        </View>

        {/* === Événement === */}
        {(quote.event_date || quote.event_location || quote.event_guests_count || quote.event_service_type) && (
          <View style={styles.eventBox}>
            <Text style={styles.eventTitle}>Détails de l&apos;événement</Text>
            <View style={styles.eventGrid}>
              {quote.event_date && (
                <View style={styles.eventItem}>
                  <Text style={styles.eventLabel}>Date & heure</Text>
                  <Text style={styles.eventValue}>{fmtDateTime(quote.event_date)}</Text>
                </View>
              )}
              {quote.event_location && (
                <View style={styles.eventItem}>
                  <Text style={styles.eventLabel}>Lieu</Text>
                  <Text style={styles.eventValue}>{quote.event_location}</Text>
                </View>
              )}
              {quote.event_guests_count && (
                <View style={styles.eventItem}>
                  <Text style={styles.eventLabel}>Personnes</Text>
                  <Text style={styles.eventValue}>{quote.event_guests_count}</Text>
                </View>
              )}
              {quote.event_service_type && (
                <View style={styles.eventItem}>
                  <Text style={styles.eventLabel}>Service</Text>
                  <Text style={styles.eventValue}>{quote.event_service_type}</Text>
                </View>
              )}
            </View>
          </View>
        )}

        {/* === Lignes (avec SECTION/NOTE/optional) === */}
        <View style={styles.table}>
          <View style={styles.th}>
            <Text style={[styles.thCell, { flex: 4 }]}>Désignation</Text>
            <Text style={[styles.thCell, { flex: 1.1, textAlign: "right" }]}>Qté / Unité</Text>
            <Text style={[styles.thCell, { flex: 1, textAlign: "right" }]}>PU CHF</Text>
            <Text style={[styles.thCell, { flex: 0.6, textAlign: "right" }]}>TVA</Text>
            <Text style={[styles.thCell, { flex: 1.1, textAlign: "right" }]}>Total CHF</Text>
          </View>

          {(quote.lines || []).map((ln, idx) => {
            // SECTION : bandeau doré pleine largeur
            if (ln.line_type === "SECTION") {
              return (
                <View key={ln.id || idx} style={styles.sectionRow}>
                  <Text style={styles.sectionText}>{ln.description || "Section"}</Text>
                </View>
              );
            }
            // NOTE : encart italique pleine largeur
            if (ln.line_type === "NOTE") {
              return (
                <View key={ln.id || idx} style={styles.noteRow}>
                  <Text style={styles.noteText}>{ln.description || ""}</Text>
                </View>
              );
            }
            // PRODUCT / FREE — ligne normale (mais marquée si optional)
            const isOptional = !!ln.optional;
            const total = (Number(ln.quantity) || 0) * (Number(ln.unit_price) || 0);
            const cellStyle = isOptional ? styles.tdMuted : styles.td;
            const qtyUnit = `${ln.quantity}${ln.unit || ""}`;
            return (
              <View key={ln.id || idx} style={styles.tr}>
                <View style={{ flex: 4 }}>
                  <Text style={cellStyle}>
                    {ln.description}{ln.product_sku ? `  (${ln.product_sku})` : ""}
                  </Text>
                  {ln.line_notes ? <Text style={styles.lineNotes}>{ln.line_notes}</Text> : null}
                  {isOptional && <Text style={styles.optionalTag}>✦ À confirmer — non chiffré dans le total</Text>}
                </View>
                <Text style={[cellStyle, { flex: 1.1, textAlign: "right" }]}>{qtyUnit}</Text>
                <Text style={[cellStyle, { flex: 1, textAlign: "right" }]}>{fmtCHF(ln.unit_price)}</Text>
                <Text style={[cellStyle, { flex: 0.6, textAlign: "right" }]}>{ln.vat_rate}%</Text>
                <Text style={[cellStyle, { flex: 1.1, textAlign: "right", fontFamily: isOptional ? "Helvetica-Oblique" : "Helvetica-Bold" }]}>
                  {isOptional ? "—" : fmtCHF(total)}
                </Text>
              </View>
            );
          })}
        </View>

        {/* === Frais additionnels === */}
        {/* Phase 101eq (Feb 2026) — Fix truncation : `wrap={false}` sur la boîte et
            sur chaque ligne pour éviter que @react-pdf coupe la section entre
            deux pages (bug observé : seule la 1ère ligne restait, les suivantes
            disparaissaient au passage de page). */}
        {fees.length > 0 && (
          <View style={styles.feesBox} wrap={false}>
            <Text style={styles.feesTitle}>Frais additionnels</Text>
            {fees.map((f, i) => {
              const isOffered = Number(f.amount || 0) === 0;
              return (
                <View key={f.id || `fee-${i}`} style={styles.feeRow} wrap={false}>
                  <Text style={styles.feeLabel}>{f.description || "Frais"}</Text>
                  <Text style={styles.feeVat}>{isOffered ? "" : `TVA ${f.vat_rate}%`}</Text>
                  <Text style={styles.feeAmount}>
                    {isOffered ? "Offert" : `${fmtCHF(f.amount)} CHF`}
                  </Text>
                </View>
              );
            })}
          </View>
        )}

        {/* === Totaux ===
            Règle métier :
            - Prix HT par défaut (B2B / revendeurs) → détail classique : Sous-total HT / Remise / Total HT / TVA / Total TTC
            - Prix TTC (particuliers, tarif boutique) → Sous-total / Remise / TOTAL TTC (dont TVA X CHF entre parenthèses)
            Phase 101eq — `wrap={false}` : bloc totaux atomique, jamais coupé au saut de page. */}
        <View style={styles.totals} wrap={false}>
          {totals.discount > 0 && (
            <>
              <View style={styles.totalRow}>
                <Text style={styles.totalLabel}>Sous-total {totals.prices_include_vat ? "TTC" : "HT"}</Text>
                <Text style={styles.totalValue}>{fmtCHF(totals.ht_initial)} CHF</Text>
              </View>
              <View style={styles.totalRow}>
                <Text style={[styles.totalLabel, { color: "#C05C47" }]}>
                  Remise {quote.discount_percent}%{quote.discount_label ? ` (${quote.discount_label})` : ""}
                </Text>
                <Text style={[styles.totalValue, { color: "#C05C47" }]}>− {fmtCHF(totals.discount)} CHF</Text>
              </View>
            </>
          )}
          {(totals.fees_total_ht || 0) > 0 && (
            <View style={styles.totalRow}>
              <Text style={styles.totalLabel}>Frais additionnels</Text>
              <Text style={styles.totalValue}>+ {fmtCHF(totals.fees_total_ht)} CHF</Text>
            </View>
          )}
          {totals.prices_include_vat ? (
            // Mode TTC (particuliers) : TOTAL TTC prominent + TVA entre parenthèses après
            <View style={styles.totalGrand}>
              <Text style={[styles.totalLabel, { fontSize: 11, color: "#1A1C1A", fontFamily: "Helvetica-Bold" }]}>TOTAL TTC</Text>
              <View style={{ alignItems: "flex-end" }}>
                <Text style={[styles.totalValue, { fontSize: 12, color: m.color }]}>{fmtCHF(totals.ttc)} CHF</Text>
                {(totals.vat || 0) > 0 && (
                  <Text style={{ fontSize: 8, color: "#575A57", marginTop: 1 }}>
                    (dont TVA {fmtCHF(totals.vat)} CHF)
                  </Text>
                )}
              </View>
            </View>
          ) : (
            // Mode HT (B2B / revendeurs) : détail classique HT + TVA + TTC
            <>
              <View style={styles.totalRow}>
                <Text style={styles.totalLabel}>Total HT</Text>
                <Text style={styles.totalValue}>{fmtCHF(totals.ht)} CHF</Text>
              </View>
              {Object.entries(totals.vatGroups || {}).map(([r, vatAmount]) => (
                <View key={r} style={styles.totalRow}>
                  <Text style={styles.totalLabel}>TVA {r}%</Text>
                  <Text style={styles.totalValue}>{fmtCHF(vatAmount)} CHF</Text>
                </View>
              ))}
              <View style={styles.totalGrand}>
                <Text style={[styles.totalLabel, { fontSize: 11, color: "#1A1C1A", fontFamily: "Helvetica-Bold" }]}>TOTAL TTC</Text>
                <Text style={[styles.totalValue, { fontSize: 12, color: m.color }]}>{fmtCHF(totals.ttc)} CHF</Text>
              </View>
            </>
          )}
        </View>

        {/* === Notes / conditions === */}
        {quote.notes && (
          <View style={styles.notesBox}>
            <Text style={styles.notesLabel}>Conditions particulières</Text>
            <Text>{quote.notes}</Text>
          </View>
        )}

        {/* === Signature (OFFER / CONFIRMATION) === */}
        {(mode === "OFFER" || mode === "CONFIRMATION") && (
          <View style={styles.signatureRow} wrap={false}>
            <View style={styles.signatureBlock}>
              <Text style={styles.signatureLabel}>{COMPANY_HEADER}</Text>
              <Text style={styles.signatureLine}>Date et signature</Text>
            </View>
            <View style={styles.signatureBlock}>
              <Text style={styles.signatureLabel}>
                {mode === "CONFIRMATION"
                  ? `Confirmé par ${quote.accepted_by || quote.client_name} le ${fmtDate(quote.accepted_at)}`
                  : `Bon pour accord — ${quote.client_company || quote.client_name}`}
              </Text>
              <Text style={styles.signatureLine}>Date et signature client</Text>
            </View>
          </View>
        )}

        <Text style={styles.footer}>
          {mode === "OFFER" && quote.validity_until && `Cette offre est valable jusqu'au ${fmtDate(quote.validity_until)} sauf mention contraire. `}
          Document généré par {COMPANY_HEADER} — {quote.reference}.
        </Text>
      </Page>
    </Document>
  );
}

export default CateringQuotePdf;
